import { createRouter, createWebHistory } from 'vue-router'
import type { RouteMeta } from 'vue-router'
import HomePage from '@/pages/HomePage.vue'
import OrganizerAuthPage from '@/pages/organizer/OrganizerAuthPage.vue'
import OrganizerDashboardPage from '@/pages/organizer/OrganizerDashboardPage.vue'
import VerifyEmailPage from '@/pages/organizer/VerifyEmailPage.vue'
import ForgotPasswordPage from '@/pages/organizer/ForgotPasswordPage.vue'
import ResetPasswordPage from '@/pages/organizer/ResetPasswordPage.vue'
import EventListPage from '@/pages/organizer/events/EventListPage.vue'
import EventCreatePage from '@/pages/organizer/events/EventCreatePage.vue'
import EventDetailPage from '@/pages/organizer/events/EventDetailPage.vue'
import EventSettingsPage from '@/pages/organizer/events/EventSettingsPage.vue'
import EventGeneralPanel from '@/pages/organizer/events/settings/EventGeneralPanel.vue'
import EventLogoPanel from '@/pages/organizer/events/settings/EventLogoPanel.vue'
import EventEmailPanel from '@/pages/organizer/events/settings/EventEmailPanel.vue'
import EventPublicSitePanel from '@/pages/organizer/events/settings/EventPublicSitePanel.vue'
import EventPublicWebsitePage from '@/pages/organizer/events/EventPublicWebsitePage.vue'
import GalleryListPage from '@/pages/organizer/events/galleries/GalleryListPage.vue'
import GalleryDetailPage from '@/pages/organizer/events/galleries/GalleryDetailPage.vue'
import EventMediaPanel from '@/pages/organizer/events/settings/EventMediaPanel.vue'
import EventAppearancePanel from '@/pages/organizer/events/EventAppearancePanel.vue'
import EditionCreatePage from '@/pages/organizer/events/editions/EditionCreatePage.vue'
import EditionDetailPage from '@/pages/organizer/events/editions/EditionDetailPage.vue'
import EditionSettingsPage from '@/pages/organizer/events/editions/EditionSettingsPage.vue'
import EditionGeneralPanel from '@/pages/organizer/events/editions/settings/EditionGeneralPanel.vue'
import EditionCancellationPanel from '@/pages/organizer/events/editions/settings/EditionCancellationPanel.vue'
import EditionSwitchRacePanel from '@/pages/organizer/events/editions/settings/EditionSwitchRacePanel.vue'
import EditionRaceRulesPanel from '@/pages/organizer/events/editions/settings/EditionRaceRulesPanel.vue'
import EditionTermsPanel from '@/pages/organizer/events/editions/settings/EditionTermsPanel.vue'
import EditionProgrammePage from '@/pages/organizer/events/editions/EditionProgrammePage.vue'
import EditionSponsorsPage from '@/pages/organizer/events/editions/EditionSponsorsPage.vue'
import RaceCreatePage from '@/pages/organizer/events/editions/races/RaceCreatePage.vue'
import RaceEditPage from '@/pages/organizer/events/editions/races/RaceEditPage.vue'
import VouchersPage from '@/pages/organizer/events/VouchersPage.vue'
import WizardFormListPage from '@/pages/organizer/wizard-forms/WizardFormListPage.vue'
import WizardFormCreatePage from '@/pages/organizer/wizard-forms/WizardFormCreatePage.vue'
import WizardFormEditPage from '@/pages/organizer/wizard-forms/WizardFormEditPage.vue'
import EditionParticipantsPage from '@/pages/organizer/participants/EditionParticipantsPage.vue'
import RaceParticipantsPage from '@/pages/organizer/participants/RaceParticipantsPage.vue'
import ParticipantEditPage from '@/pages/organizer/participants/ParticipantEditPage.vue'
import OrgGeneralPanel from '@/pages/organizer/settings/OrgGeneralPanel.vue'
import OrgBrandingPanel from '@/pages/organizer/settings/OrgBrandingPanel.vue'
import OrgPaymentsPanel from '@/pages/organizer/settings/OrgPaymentsPanel.vue'
import TeamPage from '@/pages/organizer/TeamPage.vue'
import NewsletterListPage from '@/pages/organizer/newsletters/NewsletterListPage.vue'
import NewsletterComposerPage from '@/pages/organizer/newsletters/NewsletterComposerPage.vue'
import SupportListPage from '@/pages/organizer/support/SupportListPage.vue'
import SupportDetailPage from '@/pages/organizer/support/SupportDetailPage.vue'
import DocsHubPage from '@/pages/organizer/docs/DocsHubPage.vue'
import DocsCategoryPage from '@/pages/organizer/docs/DocsCategoryPage.vue'
import DocsArticlePage from '@/pages/organizer/docs/DocsArticlePage.vue'
import CreditsPage from '@/pages/organizer/credits/CreditsPage.vue'
import ForOrganizersPage from '@/pages/public/ForOrganizersPage.vue'
import ForParticipantsPage from '@/pages/public/ForParticipantsPage.vue'
import AboutPage from '@/pages/public/AboutPage.vue'
import { prefersReducedMotion } from '@/utils/motion'
import AcceptInvitationPage from '@/pages/public/AcceptInvitationPage.vue'
import VolunteerSignUpPage from '@/pages/public/VolunteerSignUpPage.vue'
import VolunteerConfirmationPage from '@/pages/public/VolunteerConfirmationPage.vue'
import EventRacesPage from '@/pages/public/EventRacesPage.vue'
import RaceRegistrationPage from '@/pages/public/RaceRegistrationPage.vue'
import PrivacyPolicyPage from '@/pages/public/PrivacyPolicyPage.vue'
import ParticipantTermsPage from '@/pages/public/ParticipantTermsPage.vue'
import OrganizerTermsPage from '@/pages/public/OrganizerTermsPage.vue'
import { useAuth } from '@/composables/useAuth'
import { useParticipantAuth } from '@/composables/useParticipantAuth'

// Organizer zone: requires authentication and organizer role. Email
// verification is a non-blocking reminder (see OrganizerLayout.vue), not a
// route gate - see issue #205.
const ORGANIZER_META = { requiresAuth: true, requiresOrganizer: true } as const
const PARTICIPANT_META = { requiresParticipantAuth: true } as const

const router = createRouter({
  history: createWebHistory(),
  // Always land at the top of the new page, except when the browser back/forward
  // buttons restore a previous scroll position or the target route has a hash anchor.
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      // 80px clears the sticky 64px public NavBar; instant jump under reduced motion.
      return { el: to.hash, top: 80, behavior: prefersReducedMotion() ? 'auto' : 'smooth' }
    }
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomePage,
    },
    {
      path: '/privacy',
      name: 'public.privacy',
      component: PrivacyPolicyPage,
    },
    {
      path: '/terms',
      redirect: { name: 'public.terms.participants' },
    },
    {
      path: '/terms/participants',
      name: 'public.terms.participants',
      component: ParticipantTermsPage,
    },
    {
      path: '/terms/organizers',
      name: 'public.terms.organizers',
      component: OrganizerTermsPage,
    },
    {
      path: '/organizer/login',
      name: 'organizer.login',
      component: OrganizerAuthPage,
      meta: { requiresGuest: true },
    },
    {
      path: '/organizer/onboarding',
      name: 'organizer.onboarding',
      component: () => import('@/pages/organizer/OrganizerOnboardingPage.vue'),
      // No requiresAuth, no requiresOrganizer — the wizard handles its own redirect logic.
      // resolvesAuth: the wizard branches its steps on isAuthenticated/user, so auth
      // state must be resolved before it mounts even though the route itself is public.
      meta: { resolvesAuth: true },
    },
    {
      // Legacy redirect: old register URL points to the onboarding wizard
      path: '/organizer/register',
      redirect: { name: 'organizer.onboarding' },
    },
    {
      // Legacy redirect: old welcome URL points to the onboarding wizard
      path: '/organizer/welcome',
      redirect: { name: 'organizer.onboarding' },
    },
    {
      // Legacy redirect: old create-organisation URL points to the onboarding wizard
      path: '/organizer/create-organisation',
      redirect: { name: 'organizer.onboarding' },
    },
    {
      path: '/organizer/dashboard',
      name: 'organizer.dashboard',
      component: OrganizerDashboardPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/profile',
      name: 'organizer.profile',
      component: () => import('@/pages/organizer/OrganizerProfilePage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/organisations/:orgSlug/settings',
      component: () => import('@/pages/organizer/OrganisationSettingsPage.vue'),
      meta: ORGANIZER_META,
      children: [
        {
          path: '',
          name: 'organizer.organisations.settings',
          redirect: (to) => ({
            name: 'organizer.organisations.settings.general',
            params: to.params,
          }),
        },
        {
          path: 'general',
          name: 'organizer.organisations.settings.general',
          component: OrgGeneralPanel,
        },
        {
          path: 'branding',
          name: 'organizer.organisations.settings.branding',
          component: OrgBrandingPanel,
        },
        {
          path: 'payments',
          name: 'organizer.organisations.settings.payments',
          component: OrgPaymentsPanel,
        },
      ],
    },
    {
      path: '/organizer/organisations/:orgSlug/api-keys',
      name: 'organizer.organisations.api-keys',
      component: () => import('@/pages/organizer/ApiKeysPage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/verify-email',
      name: 'organizer.verify-email',
      component: VerifyEmailPage,
      meta: { requiresAuth: true },
    },
    {
      path: '/organizer/forgot-password',
      name: 'organizer.forgot-password',
      component: ForgotPasswordPage,
      meta: { requiresGuest: true },
    },
    {
      path: '/organizer/reset-password',
      name: 'organizer.reset-password',
      component: ResetPasswordPage,
      meta: { requiresGuest: true },
    },
    // Event management
    {
      path: '/organizer/events',
      name: 'organizer.events.index',
      component: EventListPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/create',
      name: 'organizer.events.create',
      component: EventCreatePage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug',
      name: 'organizer.events.show',
      component: EventDetailPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/settings',
      component: EventSettingsPage,
      meta: ORGANIZER_META,
      children: [
        {
          path: '',
          redirect: (to) => ({ name: 'organizer.events.settings.general', params: to.params }),
        },
        {
          path: 'general',
          name: 'organizer.events.settings.general',
          component: EventGeneralPanel,
        },
        {
          path: 'logo',
          name: 'organizer.events.settings.logo',
          component: EventLogoPanel,
        },
        {
          path: 'email',
          name: 'organizer.events.settings.email',
          component: EventEmailPanel,
        },
      ],
    },
    {
      path: '/organizer/events/:eventSlug/public-website',
      component: EventPublicWebsitePage,
      meta: ORGANIZER_META,
      children: [
        {
          path: '',
          redirect: (to) => ({ name: 'organizer.events.website.overview', params: to.params }),
        },
        {
          path: 'overview',
          name: 'organizer.events.website.overview',
          component: EventPublicSitePanel,
        },
        {
          path: 'media',
          name: 'organizer.events.website.media',
          component: EventMediaPanel,
        },
        {
          path: 'appearance',
          name: 'organizer.events.website.appearance',
          component: EventAppearancePanel,
        },
      ],
    },
    {
      path: '/organizer/events/:eventSlug/galleries',
      name: 'organizer.events.galleries.index',
      component: GalleryListPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/galleries/:galleryId',
      name: 'organizer.events.galleries.show',
      component: GalleryDetailPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/vouchers',
      name: 'organizer.vouchers.index',
      component: VouchersPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/create',
      name: 'organizer.editions.create',
      component: EditionCreatePage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug',
      name: 'organizer.editions.show',
      component: EditionDetailPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/settings',
      component: EditionSettingsPage,
      meta: ORGANIZER_META,
      children: [
        {
          path: '',
          redirect: (to) => ({ name: 'organizer.editions.settings.general', params: to.params }),
        },
        {
          path: 'general',
          name: 'organizer.editions.settings.general',
          component: EditionGeneralPanel,
        },
        {
          path: 'cancellation',
          name: 'organizer.editions.settings.cancellation',
          component: EditionCancellationPanel,
        },
        {
          path: 'switch-race',
          name: 'organizer.editions.settings.switch-race',
          component: EditionSwitchRacePanel,
        },
        {
          path: 'race-rules',
          name: 'organizer.editions.settings.race-rules',
          component: EditionRaceRulesPanel,
        },
        {
          path: 'terms',
          name: 'organizer.editions.settings.terms',
          component: EditionTermsPanel,
        },
      ],
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/programme',
      name: 'organizer.editions.programme',
      component: EditionProgrammePage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/sponsors',
      name: 'organizer.editions.sponsors',
      component: EditionSponsorsPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/volunteers',
      name: 'organizer.editions.volunteers',
      component: () => import('@/pages/organizer/events/editions/EditionVolunteersPage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/gear-items',
      name: 'organizer.editions.gear-items',
      component: () => import('@/pages/organizer/events/editions/EditionGearItemsPage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/aid-stations',
      name: 'organizer.editions.aid-stations',
      component: () => import('@/pages/organizer/events/editions/EditionAidStationsPage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/practical-info',
      name: 'organizer.editions.practical-info',
      component: () => import('@/pages/organizer/events/editions/PracticalInfoPage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/races/:raceSlug/form',
      name: 'organizer.races.form',
      component: () => import('@/pages/organizer/events/editions/races/RaceFormPage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/races/:raceSlug/cover-image',
      name: 'organizer.races.cover_image',
      component: () => import('@/pages/organizer/events/editions/races/RaceCoverImagePage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/races/:raceSlug/route',
      name: 'organizer.races.route',
      component: () => import('@/pages/organizer/events/editions/races/RaceRoutePage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/races/create',
      name: 'organizer.races.create',
      component: RaceCreatePage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/races/:raceSlug/edit',
      name: 'organizer.races.edit',
      component: RaceEditPage,
      meta: ORGANIZER_META,
    },
    // Wizard Form management
    // Participant management
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/participants',
      name: 'organizer.editions.participants',
      component: EditionParticipantsPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/races/:raceSlug/participants',
      name: 'organizer.races.participants',
      component: RaceParticipantsPage,
      meta: ORGANIZER_META,
    },
    // Race results (issue #261)
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/races/:raceSlug/results',
      name: 'organizer.races.results',
      component: () => import('@/pages/organizer/events/editions/races/RaceResultsPage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/races/:raceSlug/results/import',
      name: 'organizer.races.results.import.new',
      component: () => import('@/pages/organizer/events/editions/races/RaceResultImportPage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/races/:raceSlug/results/import/:importId',
      name: 'organizer.races.results.import',
      component: () => import('@/pages/organizer/events/editions/races/RaceResultImportPage.vue'),
      meta: ORGANIZER_META,
    },
    // Race results phase 2 (issue #262): edition overview and result format library/builder
    {
      path: '/organizer/events/:eventSlug/editions/:editionSlug/results',
      name: 'organizer.editions.results',
      component: () => import('@/pages/organizer/events/editions/EditionResultsOverviewPage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/result-formats',
      name: 'organizer.result-formats.index',
      component: () => import('@/pages/organizer/result-formats/ResultFormatListPage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/result-formats/create',
      name: 'organizer.result-formats.create',
      component: () => import('@/pages/organizer/result-formats/ResultFormatCreatePage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/result-formats/:formatId',
      name: 'organizer.result-formats.edit',
      component: () => import('@/pages/organizer/result-formats/ResultFormatEditPage.vue'),
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/registrations/:registrationId/edit',
      name: 'organizer.registrations.edit',
      component: ParticipantEditPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/team',
      name: 'organizer.team',
      component: TeamPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/finance',
      name: 'organizer.finance',
      component: () => import('@/pages/organizer/FinancePage.vue'),
      meta: ORGANIZER_META,
    },
    // Support
    {
      path: '/organizer/support',
      name: 'organizer.support.index',
      component: SupportListPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/support/:ticketId',
      name: 'organizer.support.show',
      component: SupportDetailPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/about',
      name: 'organizer.about',
      component: () => import('@/pages/organizer/OrganizerAboutPage.vue'),
      meta: ORGANIZER_META,
    },
    // Documentation (authenticated organizer tree)
    {
      path: '/organizer/docs',
      name: 'organizer.docs.hub',
      component: DocsHubPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/docs/:category',
      name: 'organizer.docs.category',
      component: DocsCategoryPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/docs/:category/:article',
      name: 'organizer.docs.article',
      component: DocsArticlePage,
      meta: ORGANIZER_META,
    },
    // Newsletter management
    {
      path: '/organizer/newsletters',
      name: 'organizer.newsletters.index',
      component: NewsletterListPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/organisations/:orgSlug/newsletters/:newsletterId',
      name: 'organizer.newsletters.edit',
      component: NewsletterComposerPage,
      meta: ORGANIZER_META,
    },
    // Credits management
    {
      path: '/organizer/organisations/:orgSlug/credits',
      name: 'organizer.credits',
      component: CreditsPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/wizard-forms',
      name: 'organizer.wizard-forms.index',
      component: WizardFormListPage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/wizard-forms/create',
      name: 'organizer.wizard-forms.create',
      component: WizardFormCreatePage,
      meta: ORGANIZER_META,
    },
    {
      path: '/organizer/wizard-forms/:formId',
      name: 'organizer.wizard-forms.edit',
      component: WizardFormEditPage,
      meta: ORGANIZER_META,
    },
    // Participant zone
    {
      path: '/my',
      redirect: { name: 'participant.races' },
    },
    {
      path: '/my/login',
      name: 'participant.login',
      component: () => import('@/pages/participant/ParticipantLoginPage.vue'),
      meta: { requiresParticipantGuest: true },
    },
    {
      path: '/my/races',
      name: 'participant.races',
      component: () => import('@/pages/participant/ParticipantDashboardPage.vue'),
      meta: PARTICIPANT_META,
    },
    {
      path: '/my/races/:confirmationToken',
      name: 'participant.races.detail',
      component: () => import('@/pages/participant/RegistrationDetailPage.vue'),
      meta: PARTICIPANT_META,
    },
    {
      path: '/my/profile',
      name: 'participant.profile',
      component: () => import('@/pages/participant/ParticipantProfilePage.vue'),
      meta: PARTICIPANT_META,
    },
    {
      path: '/my/payments',
      name: 'participant.payments',
      component: () => import('@/pages/participant/ParticipantPaymentsPage.vue'),
      meta: PARTICIPANT_META,
    },
    {
      path: '/for-organizers',
      name: 'public.for-organizers',
      component: ForOrganizersPage,
    },
    {
      path: '/for-participants',
      name: 'public.for-participants',
      component: ForParticipantsPage,
    },
    // About / Contact (issue #241) - public pages.
    // MUST be registered BEFORE the public slug catch-all routes.
    {
      path: '/about',
      name: 'public.about',
      component: AboutPage,
      // resolvesAuth: the contact section branches on organizer vs participant vs guest.
      meta: { resolvesAuth: true },
    },
    {
      path: '/contact',
      redirect: { name: 'public.about', hash: '#contact' },
    },
    // Documentation (public mirror — no auth guard)
    // MUST be registered BEFORE the public slug catch-all routes.
    {
      path: '/docs',
      name: 'public.docs.index',
      component: DocsHubPage,
      // No requiresAuth — public page
    },
    {
      path: '/docs/:category',
      name: 'public.docs.category',
      component: DocsCategoryPage,
      // No requiresAuth — public page
    },
    {
      path: '/docs/:category/:article',
      name: 'public.docs.article',
      component: DocsArticlePage,
      // No requiresAuth — public page
    },
    // Accept invitation — public, no auth guard.
    // MUST be registered BEFORE the public slug catch-all routes.
    {
      path: '/accept-invitation/:token',
      name: 'public.accept-invitation',
      component: AcceptInvitationPage,
      // No auth guard — the token is the access mechanism.
      // resolvesAuth: the page branches on isAuthenticated/user to show the
      // right pending-invitation view, so auth state must be resolved first.
      meta: { resolvesAuth: true },
    },
    // Public slug-based routes — must come AFTER all named routes to avoid
    // catching /organizer/..., /my/..., and other non-public paths.
    //
    // Volunteer routes MUST be registered BEFORE the general /:orgSlug/:eventSlug/:raceSlug
    // catch-all to prevent "volunteer" being treated as a race slug.
    {
      path: '/:orgSlug/:eventSlug/volunteer',
      name: 'public.volunteer',
      component: VolunteerSignUpPage,
      // No requiresAuth — public page
    },
    {
      path: '/:orgSlug/:eventSlug/volunteer/confirmation/:confirmationToken',
      name: 'public.volunteer.confirmation',
      component: VolunteerConfirmationPage,
      // No auth guard — confirmation token is the access mechanism
    },
    {
      path: '/:orgSlug/:eventSlug',
      name: 'public.event',
      component: EventRacesPage,
      // No requiresAuth — public page
    },
    {
      path: '/:orgSlug/:eventSlug/:raceSlug',
      name: 'public.registration',
      component: RaceRegistrationPage,
      // No requiresAuth — public page, but resolvesAuth: the header shows a
      // "My dashboard" link for an already-authenticated participant, which
      // needs auth state resolved on a fresh/hard page load (e.g. this page
      // opened directly from a shared link) rather than only when it happens
      // to be cached from an earlier guarded navigation this session.
      meta: { resolvesAuth: true },
    },
    {
      path: '/:orgSlug/:eventSlug/:raceSlug/confirmation/:confirmationToken',
      name: 'public.confirmation',
      component: () => import('@/pages/public/ConfirmationPage.vue'),
      // No auth guard — confirmation token is the access mechanism, but
      // resolvesAuth: this page is very often reached via a full browser
      // navigation back from Stripe's hosted checkout, which reboots the SPA
      // from scratch. Without resolving auth here, a participant who logged
      // in earlier in the wizard appears signed out in the header — even
      // though their session cookie is still valid — because the shared auth
      // state was never fetched for this fresh page load.
      meta: { resolvesAuth: true },
    },
  ],
})

// Meta flags that mean a route needs organizer/participant auth state
// resolved before the guard (or the page itself) can decide what to do.
// Routes with none of these flags are fully public and must never trigger
// a request to /api/v1/auth/user — that is the whole point of resolving
// auth lazily here instead of unconditionally on app mount.
const AUTH_RESOLUTION_META_KEYS = [
  'requiresAuth',
  'requiresOrganizer',
  'requiresGuest',
  'requiresParticipantAuth',
  'requiresParticipantGuest',
  'resolvesAuth',
] as const

function needsAuthResolution(meta: RouteMeta): boolean {
  return AUTH_RESOLUTION_META_KEYS.some((key) => meta[key] === true)
}

router.beforeEach(async (to) => {
  if (needsAuthResolution(to.meta)) {
    const { authResolved, fetchUser } = useAuth()
    // Auth is resolved at most once per page load. Subsequent guarded
    // navigations reuse the already-resolved state; login/logout update it
    // directly via useAuth's user ref.
    if (!authResolved.value) {
      await fetchUser()
    }
  }

  const { isAuthenticated, isEmailVerified, isOrganizer } = useAuth()
  const { isParticipantAuthenticated } = useParticipantAuth()

  const auth = isAuthenticated.value
  const verified = isEmailVerified.value
  const organizer = isOrganizer.value
  const participantAuth = isParticipantAuthenticated.value

  // Organiser zone guards
  if (to.meta.requiresAuth && !auth) {
    return { name: 'organizer.login' }
  }

  // Email verification never blocks access (see issue #205) - the
  // verify-email page stays reachable only as a non-blocking reminder, so an
  // already-verified user visiting it directly is sent on to the dashboard.
  if (to.meta.requiresAuth && to.name === 'organizer.verify-email' && verified) {
    return { name: 'organizer.dashboard' }
  }

  // Authenticated users without an organisation cannot access organizer routes -
  // redirect them to the onboarding wizard so they can complete onboarding,
  // regardless of email verification status.
  if (to.meta.requiresOrganizer && auth && !organizer) {
    return { name: 'organizer.onboarding' }
  }

  // Organizer login page: redirect organizers to dashboard, other
  // authenticated users without an organisation to the onboarding wizard.
  if (to.meta.requiresGuest && auth) {
    if (organizer) return { name: 'organizer.dashboard' }
    return { name: 'organizer.onboarding' }
  }

  // Participant zone guards
  if (to.meta.requiresParticipantAuth && !participantAuth) {
    return { name: 'participant.login' }
  }

  if (to.meta.requiresParticipantGuest && participantAuth) {
    return { name: 'participant.races' }
  }
})

export default router
